import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Stars, StatusBadge, ReviewTrail, ROLE_LABEL } from './reviewParts';
import { formatDateTime } from '../../lib/dateUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  ['', 'All'], ['pending', 'New'], ['handled', 'Handled'], ['with_admin', 'With Admin'],
  ['with_master_admin', 'With Master Admin'], ['published', 'Published'], ['private', 'Private'],
];

const ITActions = ({ review, onAct }) => {
  const s = review.status;
  const id = review.review_id;
  return (
    <div className="flex flex-wrap gap-2">
      {['pending', 'private', 'handled'].includes(s) && (
        <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => onAct(review, 'publish')} data-testid={`it-review-publish-${id}`}>Publish</Button>
      )}
      {s === 'published' && (
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onAct(review, 'unpublish')} data-testid={`it-review-unpublish-${id}`}>Unpublish</Button>
      )}
      {['pending', 'handled'].includes(s) && (
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onAct(review, 'private')} data-testid={`it-review-private-${id}`}>Keep private</Button>
      )}
      {['pending', 'private'].includes(s) && (
        <Button size="sm" variant="outline" className="rounded-full text-indigo-700 border-indigo-200" onClick={() => onAct(review, 'send_admin')} data-testid={`it-review-send-admin-${id}`}>Send to Admin</Button>
      )}
      {['with_admin', 'with_master_admin'].includes(s) && (
        <span className="text-xs text-muted-foreground self-center">Waiting for {s === 'with_admin' ? 'Admin' : 'Master Admin'}</span>
      )}
    </div>
  );
};

export const ITReviews = ({ token }) => {
  const [filter, setFilter] = useState('pending');
  const [reviews, setReviews] = useState([]);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/it/reviews`, { params: filter ? { status: filter } : {}, headers: { Authorization: `Bearer ${token}` } });
      setReviews(res.data);
    } catch {
      toast.error('Failed to load reviews');
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (review, action) => {
    try {
      await axios.put(`${API_URL}/it/reviews/${review.review_id}`, { action, note: notes[review.review_id] || '' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Review updated.');
      setNotes({ ...notes, [review.review_id]: '' });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update review');
    }
  };

  return (
    <Card data-testid="it-reviews-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> Reviews</CardTitle>
        <CardDescription>Publish to the About Us page, keep private, or send to Admin.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([v, label]) => (
            <Button key={v || 'all'} size="sm" variant={filter === v ? 'default' : 'outline'} className="rounded-full" onClick={() => setFilter(v)} data-testid={`it-review-filter-${v || 'all'}`}>{label}</Button>
          ))}
        </div>
        {reviews.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center" data-testid="it-reviews-empty">No reviews here.</p>}
        {reviews.map((r) => (
          <div key={r.review_id} className="border rounded-xl p-4 space-y-2 bg-white" data-testid={`it-review-${r.review_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Stars value={r.rating} size="w-4 h-4" />
                <span className="text-sm font-medium">{r.author_name}</span>
                <span className="text-xs text-muted-foreground">{ROLE_LABEL[r.author_role]} · {formatDateTime(r.created_at)}</span>
              </div>
              <StatusBadge status={r.status} testId={`it-review-status-${r.review_id}`} />
            </div>
            <p className="text-sm">{r.message}</p>
            {r.handled_note && <p className="text-xs bg-sky-50 border border-sky-200 rounded-lg p-2">Handled note: {r.handled_note}</p>}
            <ReviewTrail trail={r.trail} />
            {!['with_admin', 'with_master_admin'].includes(r.status) && (
              <Textarea rows={1} placeholder="Optional note" value={notes[r.review_id] || ''} onChange={(e) => setNotes({ ...notes, [r.review_id]: e.target.value })} data-testid={`it-review-note-${r.review_id}`} />
            )}
            <ITActions review={r} onAct={act} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
